import React, {useState, useEffect, Fragment, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';
import {IconoirCheck, IconoirXmark} from '@ey-xd/motif-icon';
import BodyKnowledgeDescription from '../../../BodyKnowledgeDescription';
import {
	numberFormattedText,
	labels,
	isEnterKeyPressed,
	GetCardTypeForBody,
	currentResource
} from '../../../../../../../util/utils';
import {numericValueRegex, keyCode} from '../../../../../../../util/uiconstants';
import Utility from '../../../../../../../util/utilityfunctions';
import Label from '../../../../../BaseComponentWrappers/MotifLabel';
import ProgressBar from '../../../../../BaseComponentWrappers/MotifProgressBar';
import Icon from '../../../../../BaseComponentWrappers/MotifIcon';
import FormField from '../../../../../BaseComponentWrappers/MotifFormField';
import Input from '../../../../../BaseComponentWrappers/MotifInput';
import {useDocumentId, useEngagementId, useUserPreferences} from '../../../../../../../util/customHooks';
import {updateSampleList} from '../../../../../../../actions/SampleList/sampleListActions';
import Loader from '../../../../../LoadingIndicator/LoadingIndicator';

export default function SampleListSize(props) {
	const {
		isAresView,
		showGuidance,
		guidanceFilter,
		options,
		headerId,
		sectionId,
		bodyId,
		getBodiesBySectionId,
		isSnapshotView,
		maxLength,
		isViewportBody,
		value,
		sampleListId,
		isLoading
	} = props;

	const sampleLabels = currentResource.sampleList;
	const dispatch = useDispatch();
	const valueOrEmpty = value || '';

	const [inputEdit, setInputEdit] = useState(false);
	const [onFocus, setOnFocus] = useState(false);

	const engagementId = useEngagementId(),
		documentId = useDocumentId(),
		userPreferences = useUserPreferences(),
		[text, setRichText] = useState(valueOrEmpty);
	const formBodyBorder = GetCardTypeForBody(options, isAresView);

	const saveChanges = (e) => {
		const currentValue = e.currentTarget.value;
		if (currentValue.match(numericValueRegex)) {
			setRichText(getPlainNumericValue(currentValue));
		} else {
			return false;
		}
	};

	const saveInputEdit = async () => {
		disableInputEdit();
		const currentValue = getValue();
		const cleanValue = currentValue == '' || currentValue < 1 ? 0 : currentValue;
		try {
			await dispatch(updateSampleList(engagementId, sampleListId, documentId, cleanValue));
		} finally {
			enableInputEdit();
			setRichText(currentValue);
		}
	};

	const disableInputEdit = () => {
		setInputEdit(false);
		setOnFocus(false);
		document.getElementById(bodyId).classList.add('greyOutArea');
	};

	const enableInputEdit = () => {
		setInputEdit(false);
		setOnFocus(false);
		document.getElementById(bodyId).classList.remove('greyOutArea');
	};

	const closeInputEdit = () => {
		setRichText(valueOrEmpty);
		setInputEdit(false);
		setOnFocus(false);
	};

	function handleOnInputKeyDown(evt) {
		if (isEnterKeyPressed(evt)) {
			saveInputEdit();
		} else if (evt.keyCode === keyCode.ESCAPE) {
			closeInputEdit();
			evt.currentTarget.blur();
		}
	}

	const isNumeric = (num) => {
		return !isNaN(num);
	};

	const getValue = () => {
		if (!inputEdit && !onFocus) {
			let formatedText = numberFormattedText(
				userPreferences.numberFormat,
				text === null || !isNumeric(text) ? '' : text
			);
			if (isSnapshotView && formatedText === '') {
				formatedText = labels.numericValuePlaceholder;
			}
			return Utility.getHtmlTextElement(formatedText, isSnapshotView);
		} else if (text === null) {
			return '';
		} else {
			return text;
		}
	};

	const getPlainNumericValue = (val) => {
		return val
			.replace(/\,/g, '')
			.replace(/\./g, '')
			.replace(/ /g, '');
	};

	useEffect(() => {
		setRichText(valueOrEmpty);
	}, [value]);

	const buildSuffix = (
		<>
			<button type="button" className="inputMotifIcon saveBtn iconBtn" onClick={saveInputEdit}>
				<Icon className="checked" src={IconoirCheck} title={labels.saveLabel} />
			</button>
			<button type="button" className="inputMotifIcon cancelBtn iconBtn" onClick={closeInputEdit}>
				<Icon className="unchecked" src={IconoirXmark} title={labels.cancelLabel} />
			</button>
		</>
	);

	const {aresViewClassName, kronosViewClassName} = useMemo(() => {
		let aresViewClassName = '';
		let kronosViewClassName = '';

		if (!inputEdit && (!text || !isFinite(text))) {
			aresViewClassName = 'labelReadonly';
		}

		if (getValue()) {
			kronosViewClassName = 'inputFieldWithNoLabel';
		}

		return {aresViewClassName, kronosViewClassName};
	}, [text, inputEdit, userPreferences]);

	return (
		<Wrapper
			className={`SectionBodyWrapper NumericBody ${formBodyBorder} ${isViewportBody ? 'BodyViewport' : ''} ${
				isAresView ? 'AresView' : 'KronosView'
			}`}
		>
			{options && (
				<BodyKnowledgeDescription
					isAresView={isAresView}
					showGuidance={showGuidance}
					guidanceFilter={guidanceFilter}
					isSnapshotView={isSnapshotView}
					bodyModel={options}
					engagementId={engagementId}
					documentId={documentId}
					headerId={headerId}
					sectionId={sectionId}
					bodyId={bodyId}
					bodyDescriptionText={options.name}
					isCustom={options.isCustom}
					isDuplicate={options.isDuplicate}
					getBodiesBySectionId={getBodiesBySectionId}
				/>
			)}
			<section className={`SectionBodyData NumericBodyData ${getValue() && 'hasValue'}`} id={bodyId}>
				{isLoading ? (
					<Centered>
						<Loader variant="circular" />
					</Centered>
				) : (
					sampleListId == null && <Centered>{sampleLabels.noSampleListAvailable}</Centered>
				)}
				{!isSnapshotView ? (
					<section className={`inputFieldWrapper ${isAresView ? aresViewClassName : kronosViewClassName}`}>
						<section className="MotifProgressBarWrapper">
							<ProgressBar isIndeterminate showLabel labelComponent={<span>{labels.saving}</span>} />
						</section>
						{!isLoading && sampleListId !== null && (
							<AmountFromField className="inputField">
								<Fragment>
									<Label position="in">{labels.numericValuePlaceholder}</Label>
									<Input
										type="text"
										maxLength={maxLength || 4}
										autoComplete="off"
										value={getValue()}
										onChange={saveChanges}
										onKeyDown={handleOnInputKeyDown}
										onFocus={() => {
											setOnFocus(true);
											setInputEdit(true);
										}}
										suffix={inputEdit && buildSuffix}
									/>
								</Fragment>
							</AmountFromField>
						)}
					</section>
				) : (
					getValue()
				)}
			</section>
		</Wrapper>
	);
}

const Centered = styled.section`
	margin: auto;
	text-align: center;
	font-size: var(--px-14);
`;

const AmountFromField = styled(FormField)`
	button.iconBtn {
		background-color: transparent;
		border: none;
		padding: 0;
		cursor: pointer;
	}

	.checked {
		--icon-color: var(${Utility.isDarkTheme() ? '--yellow-400' : '--ui-foreground-success'});
	}
	.unchecked {
		--icon-color: var(${Utility.isDarkTheme() ? '--yellow-400' : '--ui-foreground-error'});
	}
`;

const Wrapper = styled.section`
	display: block;
	&.AresView {
		.NumericBodyData {
			background-color: var(--neutrals-00white);
			padding: var(--px-27) var(--px-23) var(--px-27) var(--px-23);
			border-radius: var(--px-12);
			border: var(--px-1) solid var(--neutrals-200);
			margin-top: var(--px-20) !important;
			width: 100%;
			.inputFieldWrapper {
				.inputFieldEditWrapper {
					width: 100%;
				}
				.motif-form-field,
				.inputField {
					height: 100%;
					width: 100%;
				}
			}
		}
	}
	.SectionBodyData {
		&.NumericBodyData {
			margin-top: var(--px-20);
		}
	}
	.motif-tooltip-wrapper {
		width: calc(100% - var(--px-30));
	}
	.inputFieldWrapper {
		display: flex;
		width: var(--px-400);
		position: relative;
		.MotifProgressBarWrapper {
			display: none;
			position: absolute;
			justify-content: center;
			width: 80%;
			height: 100%;
			.motif-progress-bar-wrapper {
				height: 100%;
				width: var(--px-20);
				z-index: 1;
				justify-content: center;
				span {
					color: var(--green-500);
					font-size: var(--px-12);
				}
				.motif-progress {
					background-color: var(--green-500);
				}
			}
		}
		.editInputIconWrapper {
			display: flex;
			align-items: center;
			.inputMotifIcon {
				margin-right: var(--px-10);
				&:last-child {
					margin-right: 0;
				}
				svg {
					color: var(--neutrals-700);
					fill: none;
					path {
						fill: none;
					}
				}
				&.saveBtn {
					svg {
						color: var(--green-500);
						fill: none;
						path {
							fill: none;
						}
					}
				}
				&.cancelBtn {
					svg {
						color: var(--red-600);
						fill: none;
						path {
							fill: none;
						}
					}
				}
			}
		}
	}
	.inputField {
		width: var(--px-350);
		.inputEllipsis {
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
		}
		.motif-icon {
			display: flex !important;
			align-items: center;
			svg {
				width: 24px;
				height: 24px;
			}
		}
	}
	.inputNonEdit {
		display: block;
		width: calc(100% - var(--px-20));
		padding: 0 var(--px-7);
		position: relative;
		.motif-label {
			margin: 0;
		}
		.nonEditValue {
			display: flex;
			line-height: var(--px-26);
			font-size: var(--px-14);
			span {
				font-size: var(--px-14);
			}
			.motif-input-component {
				.motif-input-clear-button {
					svg {
						fill: var(--neutrals-900);
						display: none;
					}
				}
			}
		}
	}
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	.priorPeriodDetails {
		display: inline-block;
		margin-left: var(--px-30);
		vertical-align: top;
		margin-top: var(--px-6);
		.priorPeriodText {
			font-size: var(--px-12);
			font-weight: 300;
			display: flex;
			color: var(--neutrals-700);
			line-height: normal;
		}
		.priorPeriodValue {
			display: flex;
			font-size: var(--px-14);
			color: var(--neutrals-700);
			line-height: normal;
			margin-top: var(--px-6);
		}
		&.snapShotPriorPeriodDetails {
			vertical-align: baseline;
		}
	}
`;
